import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import {
  ArrowLeftIcon,
  HeartIcon,
  UserPlusIcon,
  UserMinusIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

// ======================================================================================
//                       ENTERPRISE INSURANCE & BENEFITS CENTER
// ======================================================================================

const InsurancePage = () => {
  const navigate = useNavigate();

  const [dependents, setDependents] = useState([
    {
      id: 1,
      name: "Spouse",
      relation: "Wife",
      coverage: "₹3,00,000",
      status: "Approved",
    },
    {
      id: 2,
      name: "Child",
      relation: "Son",
      coverage: "₹2,00,000",
      status: "Approved",
    },
  ]);

  const [claims, setClaims] = useState([
    {
      id: 1,
      name: "Hospitalization Claim",
      amount: "₹18,400",
      status: "Approved",
      date: "10 Feb 2025",
    },
    {
      id: 2,
      name: "Pharmacy Reimbursement",
      amount: "₹1,250",
      status: "Pending",
      date: "05 Mar 2025",
    },
    {
      id: 3,
      name: "Diagnostic Test Claim",
      amount: "₹3,800",
      status: "Rejected",
      date: "20 Feb 2025",
    },
  ]);

  const [newDependent, setNewDependent] = useState({
    name: "",
    relation: "",
    coverage: "",
  });

  const addDependent = () => {
    if (!newDependent.name.trim() || !newDependent.relation.trim()) {
      return alert("Please fill all fields");
    }

    setDependents([
      ...dependents,
      {
        id: Date.now(),
        ...newDependent,
        status: "Pending Approval",
      },
    ]);

    setNewDependent({ name: "", relation: "", coverage: "" });

    alert(
      "Dependent request submitted. HR/Admin will review and approve the addition."
    );
  };

  const download = (file) => alert(`Downloading: ${file}`);

  const statusColor = (status) => {
    switch (status) {
      case "Approved":
        return "text-green-700 bg-green-100";
      case "Pending Approval":
        return "text-yellow-700 bg-yellow-100";
      case "Rejected":
        return "text-red-700 bg-red-100";
      default:
        return "";
    }
  };

  // ======================================================================================
  //                                 MAIN PAGE
  // ======================================================================================

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white rounded-2xl p-6 shadow-md border border-gray-200 font-body"
    >
      {/* BACK BUTTON */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center space-x-2 mb-8 px-4 py-2 bg-gray-100 rounded-full hover:bg-gray-200 transition"
      >
        <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
        <span>Back</span>
      </button>

      {/* HEADER */}
      <div className="flex items-center space-x-3 mb-10">
        <HeartIcon className="h-12 w-12 text-rose-600" />
        <h1 className="text-3xl font-heading font-bold text-gray-900">
          Insurance & Benefits Center
        </h1>
      </div>

      {/* ================================================================================== */}
      {/* 1. PRIMARY CORPORATE INSURANCE CARD */}
      {/* ================================================================================== */}

      <div className="bg-gradient-to-r from-rose-500 to-pink-600 p-8 text-white rounded-2xl shadow-md mb-12">
        <h2 className="text-xl font-heading font-bold mb-3">Corporate Health Insurance</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <p>Policy Number: <b>POL-5523-9921</b></p>
          <p>Coverage: <b>₹5,00,000</b></p>
          <p>Provider: <b>HDFC ERGO Health</b></p>
          <p>Valid Until: <b>31 Dec 2025</b></p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6 text-sm">
          <p>Room Rent Limit: <b>₹5,000/day</b></p>
          <p>Maternity Coverage: <b>₹50,000</b></p>
          <p>Pre-existing Disease: <b>Covered after 2 years</b></p>
        </div>

        <div className="flex items-center gap-4 mt-6">
          <InsuranceButton title="E-Card" file="Health_Ecard.pdf" />
          <InsuranceButton title="Policy Certificate" file="Insurance_Certificate.pdf" />
          <InsuranceButton title="Network Hospitals" file="Network_Hospitals_List.pdf" />
        </div>
      </div>

      {/* ================================================================================== */}
      {/* 2. DEPENDENTS */}
      {/* ================================================================================== */}

      <div className="mb-12">
        <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">Dependents</h2>

        {/* List */}
        <div className="space-y-4">
          {dependents.map((d) => (
            <div
              key={d.id}
              className="p-4 border rounded-xl bg-gray-50 flex justify-between items-center"
            >
              <div>
                <p className="font-semibold text-gray-800">{d.name}</p>
                <p className="text-sm text-gray-600">{d.relation}</p>
                <p className="text-sm text-gray-500">Coverage: {d.coverage || "₹2,00,000"}</p>
              </div>

              <span
                className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColor(
                  d.status
                )}`}
              >
                {d.status}
              </span>
            </div>
          ))}
        </div>

        {/* Add Dependent */}
        <div className="mt-6 p-5 border rounded-xl shadow-sm bg-white space-y-4">
          <h3 className="font-heading font-semibold text-gray-800 mb-2">Add Dependent</h3>

          <input
            type="text"
            placeholder="Name"
            className="w-full p-3 border rounded-lg bg-gray-50"
            value={newDependent.name}
            onChange={(e) =>
              setNewDependent({ ...newDependent, name: e.target.value })
            }
          />

          <input
            type="text"
            placeholder="Relation (Spouse / Son / Daughter / Parent)"
            className="w-full p-3 border rounded-lg bg-gray-50"
            value={newDependent.relation}
            onChange={(e) =>
              setNewDependent({ ...newDependent, relation: e.target.value })
            }
          />

          <button
            onClick={addDependent}
            className="bg-rose-600 text-white font-medium px-5 py-3 rounded-xl hover:bg-rose-700 transition flex items-center space-x-2"
          >
            <UserPlusIcon className="h-5 w-5" />
            <span>Submit for Approval</span>
          </button>
        </div>
      </div>

      {/* ================================================================================== */}
      {/* 3. CLAIMS HISTORY (Enterprise Timeline UI) */}
      {/* ================================================================================== */}

      <div className="mb-12">
        <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">Claims History</h2>

        <div className="space-y-6">
          {claims.map((c) => (
            <div key={c.id} className="relative pl-10">
              <div className="absolute left-0 top-1.5 w-3 h-3 rounded-full bg-gray-400" />

              <div className="p-4 border bg-gray-50 rounded-xl">
                <div className="flex justify-between items-center">
                  <p className="font-bold text-gray-800">{c.name}</p>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${statusColor(
                      c.status
                    )}`}
                  >
                    {c.status}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">Amount: {c.amount}</p>
                <p className="text-xs text-gray-400 mt-1">{c.date}</p>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={() =>
            alert("This button will open claim submission form (future API)")
          }
          className="mt-5 bg-blue-600 text-white px-5 py-3 rounded-xl hover:bg-blue-700 transition"
        >
          File New Claim
        </button>
      </div>

      {/* ================================================================================== */}
      {/* 4. COMPANY BENEFITS */}
      {/* ================================================================================== */}

      <div className="mb-12">
        <h2 className="text-xl font-heading font-bold text-gray-900 mb-4">Company Benefits</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <BenefitCard title="Food Coupons" value="₹2,000/month" />
          <BenefitCard title="LTA" value="Eligible once per 2 years" />
          <BenefitCard title="Fuel Allowance" value="₹3,000/month" />
          <BenefitCard title="Mobile Allowance" value="₹1,200/month" />
          <BenefitCard title="WFH Allowance" value="₹1,000/month" />
          <BenefitCard title="Fitness / Wellness" value="₹8,000/year" />
        </div>
      </div>
    </motion.div>
  );
};

// ======================================================================================
// INSURANCE DOWNLOAD BUTTON COMPONENT
// ======================================================================================
const InsuranceButton = ({ title, file }) => (
  <button
    onClick={() => alert(`Downloading: ${file}`)}
    className="bg-white px-4 py-2 rounded-lg text-gray-800 shadow hover:bg-gray-100 transition flex items-center space-x-2"
  >
    <DocumentArrowDownIcon className="h-5 w-5" />
    <span>{title}</span>
  </button>
);

// ======================================================================================
// BENEFIT CARD COMPONENT
// ======================================================================================
const BenefitCard = ({ title, value }) => (
  <div className="p-5 border bg-gray-50 rounded-xl shadow-sm">
    <p className="font-heading font-semibold text-gray-800">{title}</p>
    <p className="text-sm text-gray-600 mt-1">{value}</p>
  </div>
);

export default InsurancePage;
